/**
 * Off-DOM image decode cache for playback.
 *
 * Hidden buffer slots must not mount full-size &lt;img&gt; (Chrome composites them over the
 * visible slot). Instead we warm the next still with a detached Image + decode() so the
 * on-air &lt;img&gt; can paint without a long first-frame stall on low-RAM Android.
 */

const MAX_CACHED = 3

type CacheEntry = {
  img: HTMLImageElement
  /** Generation so a superseded warm does not populate the cache. */
  generation: number
}

const readyBySrc = new Map<string, CacheEntry>()
const inflightBySrc = new Map<string, Promise<boolean>>()
let generation = 0

function touch(src: string, entry: CacheEntry): void {
  readyBySrc.delete(src)
  readyBySrc.set(src, entry)
  while (readyBySrc.size > MAX_CACHED) {
    const oldest = readyBySrc.keys().next().value
    if (oldest == null) {
      break
    }
    releaseImagePredecode(oldest)
  }
}

/**
 * True when a successful decode for this blob/object URL is held in the cache.
 */
export function isImagePredecoded(src: string): boolean {
  if (src === '') {
    return false
  }
  return readyBySrc.has(src)
}

/**
 * Refresh LRU for an already-cached decode (e.g. pin outgoing still before sticky remount).
 * Returns false when `src` is not warm — caller may {@link predecodeImageSrc}.
 */
export function touchImagePredecode(src: string): boolean {
  if (src === '') {
    return false
  }
  const entry = readyBySrc.get(src)
  if (entry == null) {
    return false
  }
  touch(src, entry)
  return true
}

/**
 * Whether a video→image advance should arm the fixed full-screen blackout.
 * Skip when the next still is already warm in the off-DOM predecode cache.
 */
export function shouldArmPostVideoImageBlackout(nextImageSrc: string | null | undefined): boolean {
  if (nextImageSrc == null || nextImageSrc === '') {
    return true
  }
  return !isImagePredecoded(nextImageSrc)
}

/**
 * Drop a cached decode (e.g. when revoking a blob: URL).
 */
export function releaseImagePredecode(src: string): void {
  if (src === '') {
    return
  }
  const entry = readyBySrc.get(src)
  if (entry != null) {
    entry.img.removeAttribute('src')
    readyBySrc.delete(src)
  }
  inflightBySrc.delete(src)
}

/** Clear all cached decodes (buffer teardown). */
export function clearImagePredecodeCache(): void {
  generation += 1
  for (const src of [...readyBySrc.keys()]) {
    releaseImagePredecode(src)
  }
  inflightBySrc.clear()
}

/**
 * Decode `src` off-DOM. Resolves true on success. Concurrent calls for the same src share one promise.
 */
export async function predecodeImageSrc(src: string): Promise<boolean> {
  if (src === '') {
    return false
  }
  if (readyBySrc.has(src)) {
    const existing = readyBySrc.get(src)!
    touch(src, existing)
    return true
  }
  const existingInflight = inflightBySrc.get(src)
  if (existingInflight != null) {
    return existingInflight
  }

  const gen = generation
  const promise = (async (): Promise<boolean> => {
    if (typeof Image === 'undefined') {
      return false
    }
    const img = new Image()
    img.decoding = 'async'
    img.src = src
    try {
      await img.decode()
    }
    catch {
      img.removeAttribute('src')
      return false
    }
    if (gen !== generation) {
      img.removeAttribute('src')
      return false
    }
    touch(src, { img, generation: gen })
    return true
  })()

  inflightBySrc.set(src, promise)
  try {
    return await promise
  }
  finally {
    if (inflightBySrc.get(src) === promise) {
      inflightBySrc.delete(src)
    }
  }
}

/**
 * Ensure the on-air &lt;img&gt; has decoded before revealing it.
 * Uses the cache when warm; otherwise decodes the element in place.
 */
export async function ensureHtmlImageDecoded(el: HTMLImageElement, src: string): Promise<boolean> {
  if (src === '') {
    return false
  }
  if (el.src !== src && el.getAttribute('src') !== src) {
    el.src = src
  }
  if (isImagePredecoded(src)) {
    try {
      await el.decode()
      return true
    }
    catch {
      // Fall through — cache hit does not always transfer to a new element on every WebView.
    }
  }
  try {
    await el.decode()
    if (!readyBySrc.has(src)) {
      // Keep a detached twin so the next swap for this src stays warm.
      void predecodeImageSrc(src)
    }
    return true
  }
  catch {
    return false
  }
}
